import type { PropertyRecord } from "../types/portfolio";
import type { TeamContact } from "../types/entities";

export interface ResolvedRecipient {
  name: string;
  email: string;
  source: "team-contact" | "property-manager" | "manual-fallback";
}

/**
 * Resolve the best recipient for a notice email given the recipient role,
 * property context, and team contacts. Returns null if no contact with email found.
 */
export function resolveRecipient(
  recipientRole: string,
  property: PropertyRecord | undefined,
  teamContacts: TeamContact[],
): ResolvedRecipient | null {
  // PM-related: resolve from property's PM fields
  if (recipientRole === "pm") {
    if (property?.propertyManagerName && property?.propertyManagerEmail) {
      return { name: property.propertyManagerName, email: property.propertyManagerEmail, source: "property-manager" };
    }
    return null;
  }

  // Team contact roles — find matching contact with email
  const matchRoles: string[] =
    recipientRole === "mortgage-broker" ? ["mortgage-broker", "broker"] :
    recipientRole === "broker" ? ["broker", "mortgage-broker"] :
    [recipientRole];

  // Prefer contact linked to this property's structure
  const structureId = property?.structureId;
  if (structureId) {
    for (const role of matchRoles) {
      const linked = teamContacts.find(
        (c) => c.role === role && c.email && (c.associatedStructureIds ?? []).includes(structureId),
      );
      if (linked) return { name: linked.name, email: linked.email!, source: "team-contact" };
    }
  }

  // Fall back to any matching contact with email
  for (const role of matchRoles) {
    const contact = teamContacts.find((c) => c.role === role && c.email);
    if (contact) return { name: contact.name, email: contact.email!, source: "team-contact" };
  }

  return null;
}
